import React, { createContext, useContext, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { THEME_COLORS, DEFAULT_THEME_BY_ROLE, getThemeHexColor } from '@/lib/themeColors';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const { userProfile, updateProfile } = useAuth();

  // Get current theme from user preferences or fall back to role default
  const currentTheme = useMemo(() => {
    if (userProfile?.preferences?.ui_theme) {
      return userProfile.preferences.ui_theme;
    }
    if (userProfile?.active_role) {
      return DEFAULT_THEME_BY_ROLE[userProfile.active_role] || 'blue';
    }
    return 'blue';
  }, [userProfile?.preferences?.ui_theme, userProfile?.active_role]);

  // Update the PWA theme-color meta tag when theme changes
  useEffect(() => {
    const themeColor = getThemeHexColor(currentTheme);
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColor);
    }
  }, [currentTheme]);

  // Set the user's theme preference - useCallback to avoid stale closure
  const setTheme = useCallback(async (themeId) => {
    if (!THEME_COLORS[themeId]) {
      console.error(`Invalid theme: ${themeId}`);
      return;
    }

    if (!userProfile) {
      console.error('Cannot set theme: no user profile');
      return;
    }

    try {
      await updateProfile({
        preferences: {
          ...userProfile.preferences,
          ui_theme: themeId
        }
      });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  }, [userProfile, updateProfile]);

  // Get the active color scheme based on current theme
  const getActiveColors = useCallback(() => {
    return THEME_COLORS[currentTheme] || THEME_COLORS.blue;
  }, [currentTheme]);

  const value = useMemo(() => ({
    currentTheme,
    setTheme,
    getActiveColors,
  }), [currentTheme, setTheme, getActiveColors]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Fallback hook that returns default colors when context is unavailable
// Useful for components that may render before auth is ready
export function useThemeColors(role) {
  const context = useContext(ThemeContext);

  if (context) {
    return context.getActiveColors();
  }

  // Fallback when outside ThemeProvider
  const defaultTheme = role ? DEFAULT_THEME_BY_ROLE[role] : 'blue';
  return THEME_COLORS[defaultTheme] || THEME_COLORS.blue;
}
